if (!customElements.get('wnc-collection')) {
  customElements.define('wnc-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.columns = { desktop: this.dataset.desktopColumns === '3' ? 3 : 4, mobile: this.dataset.mobileColumns === '1' ? 1 : 2 };
      try {
        const saved = JSON.parse(sessionStorage.getItem('wnc.collection.grid') || 'null');
        if (saved?.desktop === 3 || saved?.desktop === 4) this.columns.desktop = saved.desktop;
        if (saved?.mobile === 1 || saved?.mobile === 2) this.columns.mobile = saved.mobile;
      } catch (_) {}
      this.variantData = new WeakMap();
      this.installmentPrices = new WeakMap();
      this.addEventListener('click', event => this.onClick(event), { ...options, capture: true });
      this.addEventListener('change', () => this.schedule(), options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !this.desktopFilters) return;
        const opened = this.inlineFilters?.querySelector('details[open]');
        if (!opened) return;
        this.closeFilters();
        opened.querySelector('summary')?.focus();
        event.stopPropagation();
      }, { ...options, capture: true });
      document.addEventListener('click', event => {
        if (this.desktopFilters && !this.inlineFilters?.contains(event.target)) this.closeFilters();
      }, options);
      window.addEventListener('resize', () => this.schedule(), options);
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this, { childList: true, subtree: true });
      this.refresh();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      clearTimeout(this.refreshTimer);
      if (this.accordionHome && this.accordion) this.accordionHome.appendChild(this.accordion);
      this.inlineFilters?.remove();
      this.inlineFilters = null;
      this.accordion = null;
      this.accordionHome = null;
    }

    schedule() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 0);
    }

    refresh() {
      if (!this.isConnected) return;
      const controls = this.querySelector('[data-wnc-grid-controls]');
      const utilities = this.querySelector('.collection-facets__inner');
      if (controls && utilities) {
        if (controls.parentElement !== utilities) utilities.appendChild(controls);
        controls.hidden = false;
      }
      this.updateFilterPlacement();
      this.updateGrid();
      this.updateProgress();
      this.querySelectorAll('.product-card--collection').forEach(card => this.updateInstallments(card));
    }

    updateFilterPlacement() {
      if (!this.accordion?.isConnected) {
        this.accordion = this.querySelector('.facets__drawer accordion-default');
        this.accordionHome = this.accordion?.parentElement;
        const form = this.accordion?.closest('form');
        if (!form) return;
        this.inlineFilters = document.createElement('div');
        this.inlineFilters.className = 'wnc-collection-inline-filters';
        form.appendChild(this.inlineFilters);
      }
      if (!this.accordion) return;
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      const destination = desktop ? this.inlineFilters : this.accordionHome;
      if (this.accordion.parentElement !== destination) {
        this.closeFilters();
        destination.appendChild(this.accordion);
      }
      this.desktopFilters = desktop;
      this.inlineFilters.hidden = !desktop;
    }

    closeFilters() {
      this.accordion?.querySelectorAll('details.facets__accordion-section').forEach(details => {
        details.open = false;
        details.classList.remove('is-active');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      });
    }

    onClick(event) {
      const view = event.target.closest('[data-wnc-grid-view]');
      if (view && this.contains(view)) {
        const mode = window.matchMedia('(min-width: 750px)').matches ? 'desktop' : 'mobile';
        this.columns[mode] = Number(view.dataset[mode]);
        try { sessionStorage.setItem('wnc.collection.grid', JSON.stringify(this.columns)); } catch (_) {}
        this.updateGrid();
        return;
      }
      if (!this.desktopFilters) return;
      const summary = event.target.closest('.wnc-collection-inline-filters .facets__accordion-btn');
      if (!summary || !this.contains(summary)) return;
      const details = summary.closest('details');
      const wasOpen = details.open;
      event.preventDefault();
      event.stopPropagation();
      this.closeFilters();
      details.open = !wasOpen;
      details.classList.toggle('is-active', !wasOpen);
      summary.setAttribute('aria-expanded', String(!wasOpen));
    }

    updateGrid() {
      this.style.setProperty('--wnc-desktop-columns', this.columns.desktop);
      this.style.setProperty('--wnc-mobile-columns', this.columns.mobile);
      const mode = window.matchMedia('(min-width: 750px)').matches ? 'desktop' : 'mobile';
      this.querySelectorAll('[data-wnc-grid-view]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset[mode]) === this.columns[mode]));
        button.setAttribute('aria-label', button.dataset[mode + 'Label']);
      });
    }

    updateProgress() {
      const progress = this.querySelector('[data-wnc-progress]');
      const grid = this.querySelector('#product-grid');
      if (!progress || !grid) return;
      const total = Number(progress.dataset.total);
      const shown = Math.min(grid.querySelectorAll('.product-card').length, total);
      const label = progress.querySelector('[data-wnc-progress-text]');
      const text = label.dataset.template.replace('__SHOWN__', shown);
      if (label.textContent !== text) label.textContent = text;
      const bar = progress.querySelector('progress');
      bar.max = total || 1;
      bar.value = shown;
    }

    updateInstallments(card) {
      const installment = card.querySelector('[data-wnc-installments]');
      if (!installment) return;
      let variants = card.variantsObj || this.variantData.get(card);
      if (!variants) {
        try { variants = JSON.parse(card.querySelector('[data-product-variants-json]')?.textContent || '[]'); } catch (_) { return; }
        this.variantData.set(card, variants);
      }
      const selectedId = card.findCurrentVariantFromOptionRadioInputs?.().currentVariantId || card.querySelector('input[name="id"]')?.value;
      const selected = variants.find(variant => String(variant.id) === String(selectedId));
      if (!selected || !Number.isFinite(Number(selected.price))) return;
      const cents = Math.round(Number(selected.price) / 6);
      const locale = document.documentElement.lang || 'pt-BR';
      const currency = installment.dataset.currency || 'BRL';
      const renderKey = [cents, locale, currency, installment.dataset.template].join('|');
      if (this.installmentPrices.get(installment) === renderKey) return;
      this.installmentPrices.set(installment, renderKey);
      const amount = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
      const html = installment.dataset.template.replace('__AMOUNT__', amount);
      if (installment.innerHTML !== html) installment.innerHTML = html;
    }
  });
}
